import React from "react";
import PropTypes from "prop-types";
import { Alert, Checkbox, Select, TextInput } from "@opendate-app/setlist";
import { useMembershipProductNewContext } from "./MembershipProductNewContainer";
import { checkedFrom, FieldError, ToggleField } from "./formControls";
import { slugify } from "./membershipFormLogic";
import SwitchToField from "./SwitchToField";

const MAX_VENUE_CHECKBOXES = 6;

const SellingSection = ({ errors, disabled, onSlugEdited }) => {
  const { team, venueOwnerships, membershipProduct, updateMembershipProduct } = useMembershipProductNewContext();
  const venueIds = membershipProduct.venue_ownership_ids || [];
  const venueOptions = venueOwnerships.map((vo) => ({ value: vo.id, label: vo.name }));
  const live = membershipProduct.status === "active";

  const toggleVenue = (id, checked) =>
    updateMembershipProduct({ venue_ownership_ids: checked ? [...venueIds, id] : venueIds.filter((v) => v !== id) });

  return (
    <>
      <div className="membership-grid-2">
        {venueOwnerships.length > MAX_VENUE_CHECKBOXES ? (
          <div className="membership-field">
            <Select
              id="membership-venues"
              label="Venues"
              required
              multiple
              searchable
              disabled={disabled}
              placeholder="Select venues"
              options={venueOptions}
              value={venueIds}
              onChange={(value) => updateMembershipProduct({ venue_ownership_ids: value || [] })}
              hint={errors.venues ? undefined : "Benefits work at these venues."}
            />
            <FieldError>{errors.venues}</FieldError>
          </div>
        ) : (
          <fieldset className="membership-field membership-venues">
            <legend className="membership-label">
              Venues <span className="membership-required">*</span>
            </legend>
            <div className="membership-venue-list">
              {venueOwnerships.map((vo) => (
                <Checkbox
                  key={vo.id}
                  id={`membership-venue-${vo.id}`}
                  label={vo.name}
                  checked={venueIds.includes(vo.id)}
                  onChange={(value) => toggleVenue(vo.id, checkedFrom(value))}
                />
              ))}
            </div>
            {errors.venues ? <FieldError>{errors.venues}</FieldError> : <p className="membership-hint">Benefits work at these venues.</p>}
          </fieldset>
        )}
        <div className="membership-field">
          <Select
            id="membership-billing-venue"
            label="Billing venue"
            required
            clearable
            searchable={venueOwnerships.length > MAX_VENUE_CHECKBOXES}
            disabled={disabled}
            placeholder="Select a venue"
            options={venueOptions}
            value={membershipProduct.billing_venue_ownership_id}
            onChange={(value) => updateMembershipProduct({ billing_venue_ownership_id: value || null })}
            hint={errors.billing ? undefined : "Dues pay out to this venue, and its tax settings apply."}
          />
          <FieldError>{errors.billing}</FieldError>
        </div>
      </div>

      <ToggleField
        id="membership-sell-in-widget"
        title="Sell in the event ticketing widget"
        description="Fans can add this membership when they buy tickets, at the ticketing price."
        checked={!!membershipProduct.add_to_event_ticketing}
        onChange={(checked) => updateMembershipProduct({ add_to_event_ticketing: checked })}
      />
      <ToggleField
        id="membership-listed-publicly"
        title="List on the public memberships page"
        description="Off keeps it reachable only by its private link."
        checked={membershipProduct.listed_publicly !== false}
        onChange={(checked) => updateMembershipProduct({ listed_publicly: checked })}
      />
      <ToggleField
        id="membership-giftable"
        title="Can be bought as a gift"
        description="Fans can buy it for someone else. The recipient gets the membership; the buyer pays."
        checked={!!membershipProduct.giftable}
        onChange={(checked) => updateMembershipProduct({ giftable: checked })}
      />
      {membershipProduct.giftable ? (
        <div className="membership-toggle-nested">
          <ToggleField
            id="membership-gift-keep-paying"
            title="Let gift buyers keep paying"
            description="Buyers can choose to renew the gift on their card. Off makes every gift one-time."
            checked={!!membershipProduct.gift_buyers_can_keep_paying}
            onChange={(checked) => updateMembershipProduct({ gift_buyers_can_keep_paying: checked })}
          />
        </div>
      ) : null}
      <ToggleField
        id="membership-send-welcome-email"
        title="Send a welcome email"
        description="Emails new members when they join. Off if you send your own welcome."
        checked={membershipProduct.send_welcome_email !== false}
        onChange={(checked) => updateMembershipProduct({ send_welcome_email: checked })}
      />

      <SwitchToField />

      <TextInput
        id="membership-slug"
        label="Private link"
        leadingText={`${window.location.host}/m/${team.id}/`}
        placeholder="patron-circle"
        value={membershipProduct.slug || ""}
        error={errors.slug}
        onBlur={(e) => e.target.value && updateMembershipProduct({ slug: slugify(e.target.value) })}
        hint={
          live
            ? "Live now. Changing it breaks links you've already shared."
            : "Fans can buy from this link once the membership is published."
        }
        onChange={(e) => {
          onSlugEdited();
          updateMembershipProduct({ slug: e.target.value });
        }}
      />

      <Alert
        color="brand"
        description="Need to charge tax on this membership? Your Customer Success rep can set up tax rates for the billing venue."
      />
    </>
  );
};

SellingSection.propTypes = {
  errors: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  onSlugEdited: PropTypes.func.isRequired,
};

SellingSection.defaultProps = {
  disabled: false,
};

export default SellingSection;
